import Image from 'next/image';
import { useCallback, useState, Fragment, useEffect } from 'react';
import { Transition } from '@headlessui/react';

import CloseIcon from '~/public/toaster/close.png';
import { SURVEY_DISMISS_TIMEOUT } from '~/constants/survey';

/*
 * Simulates a "modal" component for the NPS survey.
 *
 * Modal component cannot be used in this component. There are a few reasons:
 *
 * 1. Survey does not use a backdrop
 * 2. Survey must not be closed if the user clicks outside of the container
 * 3. Survey must be displayed at the bottom left of the screen
 */
export function SurveyContainer({
  title,
  children,
  isSubmitted,
  className,
  onClose,
}: Readonly<
  React.PropsWithChildren<{
    title: string;
    isSubmitted: boolean;
    className?: string;
    onClose?: () => void;
  }>
>) {
  const [isOpen, setIsOpen] = useState(true);

  const handleOnClose = useCallback(() => {
    // Only save the dismiss action if form is not submitted. This will prevent
    // an unnecessary dismiss record and the user will be able to show the survey again
    // based on the response submission only.
    if (!isSubmitted) {
      onClose?.();
    }

    setIsOpen(false);
  }, [isSubmitted, onClose]);

  useEffect(() => {
    // Close the survey after a few seconds if the user has submitted the form.
    if (isSubmitted) {
      setTimeout(() => {
        setIsOpen(false);
      }, SURVEY_DISMISS_TIMEOUT);
    }
  }, [isSubmitted]);

  return (
    <Transition
      appear={true}
      show={isOpen}
      enter="transition-opacity duration-200"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
      as={Fragment}
    >
      <div
        className={`fixed z-[9999] flex flex-col gap-y-2 rounded bg-white px-4 py-6
          shadow-[0px_-8px_40px_0px_#0000001a] md:w-auto md:max-w-[520px] md:items-start
          md:shadow-[0px_20px_40px_0px_#0000001a] ${className}`}
      >
        <div className="flex w-full justify-between gap-x-8">
          <h1 className="font-header text-xl font-semibold">{title}</h1>
          <Image
            src={CloseIcon}
            alt="Close"
            height={20}
            width={20}
            className="h-5 w-auto cursor-pointer"
            onClick={handleOnClose}
          />
        </div>
        {children}
      </div>
    </Transition>
  );
}
